/**
 * The page as it was printed: one page of the scan on a desk, with the pager that turns it.
 *
 * The pager turns both desks of the Read board, so the page number is the board's and not
 * this panel's: what was read is always laid beside the page it was read off. The page is
 * drawn as wide as the desk and scrolls down it, because it is there to be checked note
 * against note, and a whole page fitted to half a window is too small to check.
 */

import { useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { SHEET } from "./ScoreCanvas";
import { drawn } from "../lib/pdf-pages";

/** The widest a page is drawn, in CSS pixels: wider than this the staves outgrow the desk. */
const WIDEST = 900;

function Turn({
  label,
  disabled,
  onTurn,
  children,
}: {
  readonly label: string;
  readonly disabled: boolean;
  onTurn(): void;
  readonly children: string;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onTurn}
      className="h-7 w-7 rounded text-[13px] text-shell-text hover:bg-shell-hot hover:text-shell-lit disabled:text-shell-seam disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}

/** The scan's pages turned one at a time; the board's one pager, over whichever desk shows it. */
export function Pager({
  page,
  pages,
  onPage,
}: {
  readonly page: number;
  readonly pages: number;
  onPage(page: number): void;
}): JSX.Element {
  return (
    <span className="ml-auto flex items-center gap-1 font-mono text-[11px] text-shell-text">
      <Turn label="The page before" disabled={page <= 1} onTurn={() => onPage(page - 1)}>
        ‹
      </Turn>
      <span>
        {page} of {pages}
      </span>
      <Turn label="The next page" disabled={page >= pages} onTurn={() => onPage(page + 1)}>
        ›
      </Turn>
    </span>
  );
}

export interface ReadSheetProps {
  readonly pdf: PDFDocumentProxy;
  /** The page on the desk, 1-based. */
  readonly page: number;
  onPage(page: number): void;
}

export default function ReadSheet({ pdf, page, onPage }: ReadSheetProps): JSX.Element {
  const ground = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const at = ground.current;
    if (at === null) return undefined;
    const measured = (): void => setWidth(Math.min(Math.floor(at.clientWidth) - 40, WIDEST));
    measured();
    const watching = new ResizeObserver(measured);
    watching.observe(at);
    return () => watching.disconnect();
  }, []);

  // One drawing at a time on the canvas: each waits for the one before to finish or stop.
  const last = useRef<Promise<void>>(Promise.resolve());
  useEffect(() => {
    const on = canvas.current;
    if (on === null || width <= 0) return undefined;
    let wanted = true;
    let stop = (): void => undefined;
    last.current = last.current.then(async () => {
      if (!wanted) return;
      const drawing = await drawn(pdf, page, on, width);
      stop = drawing.stop;
      if (!wanted) stop();
      await drawing.done;
    });
    // A page that cannot be drawn leaves the desk bare, and the next page is still drawn.
    last.current = last.current.catch(() => undefined);
    return () => {
      wanted = false;
      stop();
    };
  }, [pdf, page, width]);

  // In braces: a browser whose `scrollTo` answers with a promise would hand it to React.
  useEffect(() => {
    ground.current?.scrollTo({ top: 0 });
  }, [page]);

  return (
    <section className="flex min-h-[20rem] flex-1 flex-col overflow-hidden rounded-md border border-shell-edge bg-shell-bar shadow-chrome">
      <header className="flex shrink-0 items-center gap-2 border-b border-shell-edge px-2.5 py-1">
        <h2 className="truncate text-[12px] font-medium text-shell-lit">The page</h2>
        <Pager page={page} pages={pdf.numPages} onPage={onPage} />
      </header>
      <div ref={ground} className="min-h-0 flex-1 overflow-auto bg-canvas p-5">
        <canvas
          ref={canvas}
          aria-label={`Page ${page} of the scan`}
          className={`mx-auto block ${SHEET}`}
          style={{ width: width > 0 ? `${width}px` : undefined }}
        />
      </div>
    </section>
  );
}
